import { map, catchError, switchMap, filter, mergeMap } from "rxjs/operators";
import { of } from "rxjs";
import type { RootEpic } from "../types";
import {
  getPermissionsRequest,
  getPermissionsSuccess,
  getPermissionsFailure,
  getDocumentUsersPermissionsRequest,
  getDocumentUsersPermissionsSuccess,
  getDocumentUsersPermissionsFailure,
  getFolderUsersPermissionsRequest,
  getFolderUsersPermissionsSuccess,
  getFolderUsersPermissionsFailure,
  mappingPermissionRequest,
  mappingPermissionSuccess,
  mappingPermissionFailure,
  addPermissionRequest,
  addPermissionSuccess,
  addPermissionFailure,
  delPermissionRequest,
  delPermissionSuccess,
  delPermissionFailure,
  roleMappingLabelRequest,
  roleMappingLabelSuccess,
  roleMappingLabelFailure,
  getPermissionUserFolderRequest,
  getPermissionUserFolderSuccess,
  getPermissionUserFolderFailure,
  getRolesRequest,
  getRolesSuccess,
  getRolesFailure,
  getRolePermissionFolderRequest,
  getRolePermissionFolderSuccess,
  getRolePermissionFolderFailure,
  revokeOwnershipRequest,
  revokeOwnershipSuccess,
  revokeOwnershipFailure,
  getFileUserPermissionsRequest,
  getFileUserPermissionsSuccess,
  getFileUserPermissionsFailure,
} from "./permissionSlice";
import { UploadFileService } from "@/services/UploadFileService";
import { FolderService } from "@/services/FolderService";
import { uploadFileActions } from "../uploadFiles/uploadFileSlice";

export const getPermissionsEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getPermissionsRequest.type),
    switchMap((action: any) => {
      const payload = action.payload || {};
      const page = payload.page ?? 1;
      const pageSize = payload.pageSize ?? 20;
      const type = payload.type;
      return UploadFileService.Get.getFullPermission({
        page,
        pageSize,
        type,
      }).pipe(
        map((res: any) => getPermissionsSuccess(res)),
        catchError((err) =>
          of(
            getPermissionsFailure(err?.message || "Error fetching permissions")
          )
        )
      );
    })
  );

export const getDocumentUsersPermissionsEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getDocumentUsersPermissionsRequest.type),
    switchMap((action: any) => {
      const payload = action.payload || {};
      const documentId = payload.documentId;
      return UploadFileService.Get.getDocumentUsersPermissions(documentId).pipe(
        map((res: any) => getDocumentUsersPermissionsSuccess(res)),
        catchError((err) =>
          of(
            getDocumentUsersPermissionsFailure(
              err?.message || "Error fetching document permissions"
            )
          )
        )
      );
    })
  );

export const mappingPermissionEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === mappingPermissionRequest.type),
    switchMap((action: any) => {
      const payload = action.payload;
      const { type, userId, permissionIds, fileIds, labelIds, documentId } =
        payload;
      console.log(
        "type, mappingId, userId, permissionIds, labelId, documentId",
        payload
      );
      const postPayload = {
        userId,
        permissionIds,
        ...(fileIds && fileIds.length > 0 ? { fileIds } : {}),
        ...(labelIds && labelIds.length > 0 ? { labelIds } : {}),
      };

      return UploadFileService.Post.mappingPermission(postPayload).pipe(
      ).pipe(
        map((res: any) => mappingPermissionSuccess(res)),
        switchMap((res: any) => {
          if (type === "Document") {
            return [
              mappingPermissionSuccess(res),
              getDocumentUsersPermissionsRequest({ documentId: documentId }),
            ];
          } else {
            return [
              mappingPermissionSuccess(res),
              getRolePermissionFolderRequest({ folderId: labelIds[0] }),
              getFolderUsersPermissionsRequest({ folderId: labelIds[0] }),
            ];
          }
        }),
        catchError((err) =>
          of(
            mappingPermissionFailure(err?.message || "Error mapping permission")
          )
        )
      );
    })
  );

export const getFolderUsersPermissionsEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getFolderUsersPermissionsRequest.type),
    mergeMap((action: any) => {
      const payload = action.payload || {};
      const folderId = payload.folderId;
      return UploadFileService.Get.getFolderUsersPermissions(folderId).pipe(
        map((res: any) => getFolderUsersPermissionsSuccess({
          folderId,
          data: res,
        })
        ),
        catchError((err) =>
          of(
            getFolderUsersPermissionsFailure(
              err?.message || "Error fetching folder permissions"
            )
          )
        )
      );
    })
  );

export const addPermissionEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === addPermissionRequest.type),
    switchMap((action: any) => {
      const { id, permissionId } = action.payload;
      return UploadFileService.Put.addPermission(id, permissionId).pipe(
        map((res: any) => addPermissionSuccess(res)),
        catchError((err) =>
          of(addPermissionFailure(err?.message || "Error adding permission"))
        )
      );
    })
  );

export const delPermissionEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === delPermissionRequest.type),
    switchMap((action: any) => {
      const { id, permissionId } = action.payload;
      return UploadFileService.Put.delPermission(id, permissionId).pipe(
        map((res: any) => delPermissionSuccess(res)),
        catchError((err) =>
          of(delPermissionFailure(err?.message || "Error deleting permission"))
        )
      );
    })
  );

export const getPermissionUserFolderEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getPermissionUserFolderRequest.type),
    mergeMap((action: any) => {
      const { folderId, userId } = action.payload;
      return UploadFileService.Get.getFolderUserPermissions(
        folderId,
        userId
      ).pipe(
        map((res: any) => getPermissionUserFolderSuccess({
          folderId,
          data: res,
        })),
        catchError((err) =>
          of(
            getPermissionUserFolderFailure(
              err?.message || "Error fetching permission user folder"
            )
          )
        )
      );
    })
  );

export const getFileUserPermissionsEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(
      (action) =>
        action.type === getFileUserPermissionsRequest.type
    ),
    mergeMap((action: any) => {
      const { fileId, documentId, userId } = action.payload;

      return UploadFileService.Get.getFileUserPermissions(
        documentId,
        userId
      ).pipe(
        map((res: any) =>
          getFileUserPermissionsSuccess({
            fileId,
            data: res,
          })
        ),
        catchError((err) =>
          of(
            getFileUserPermissionsFailure(
              err?.message || "Error fetching permission user file"
            )
          )
        )
      );
    })
  );

export const getRolesEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getRolesRequest.type),
    switchMap(() => {
      return UploadFileService.Get.getRoles().pipe(
        map((res: any) => getRolesSuccess(res)),
        catchError((err) =>
          of(getRolesFailure(err?.message || "Error fetching roles"))
        )
      );
    })
  );

export const roleMappingLabelEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === roleMappingLabelRequest.type),
    switchMap((action: any) => {
      const { labelId_body, roleId, userId, transferedUser } = action.payload;
      const body = {
        labelId: labelId_body,
        roleId,
        userId,
      };
      return UploadFileService.Post.roleMappingLabel(body).pipe(
        switchMap((res: any) => {
          // Sau khi transfer ownership thành công, call transferOwner API nếu có transferedUser
          if (transferedUser) {
            return FolderService.Put.transferOwner(labelId_body, transferedUser).pipe(
              switchMap(() => [
                roleMappingLabelSuccess(res),
                getRolePermissionFolderRequest({
                  folderId: labelId_body,
                }),
                uploadFileActions.setLastUploadAt(Date.now()),
              ]),
              catchError((err: any) =>
                of(
                  roleMappingLabelFailure(
                    err?.message || "Error transferring ownership"
                  )
                )
              )
            );
          }
          // Nếu không có transferedUser, chỉ refresh permissions
          return [
            roleMappingLabelSuccess(res),
            getRolePermissionFolderRequest({
              folderId: labelId_body,
            }),
          ];
        }),
        catchError((err) =>
          of(
            roleMappingLabelFailure(
              err?.message || "Error in role mapping label"
            )
          )
        )
      );
    })
  );

export const getRolePermissionFolderEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getRolePermissionFolderRequest.type),
    switchMap((action: any) => {
      const { folderId } = action.payload;
      return UploadFileService.Get.getFolderUsersPermissionsRole(folderId).pipe(
        map((res: any) => getRolePermissionFolderSuccess(res)),
        catchError((err) =>
          of(
            getRolePermissionFolderFailure(
              err?.message || "Error fetching role permission folder"
            )
          )
        )
      );
    })
  );

export const revokeOwnershipEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === revokeOwnershipRequest.type),
    switchMap((action: any) => {
      const { userId, labelId, transferedUser } = action.payload;
      return UploadFileService.Delete.revokeOwnership(userId, labelId).pipe(
        switchMap((res: any) => {
          if (transferedUser) {
            return FolderService.Put.transferOwner(labelId, transferedUser).pipe(
              switchMap(() => [
                revokeOwnershipSuccess(res),
                getRolePermissionFolderRequest({ folderId: labelId }),
                uploadFileActions.setLastUploadAt(Date.now()),
              ]),
              catchError((err: any) =>
                of(
                  revokeOwnershipFailure(
                    err?.message || "Error transferring ownership"
                  )
                )
              )
            );
          }
          // Nếu không có transferedUser, chỉ revoke
          return [
            revokeOwnershipSuccess(res),
            getRolePermissionFolderRequest({ folderId: labelId }),
          ];
        }),
        catchError((err) =>
          of(
            revokeOwnershipFailure(
              err?.message || "Error revoking ownership"
            )
          )
        )
      );
    })
  );

export const permissionEpics = [
  getPermissionsEpic$,
  getDocumentUsersPermissionsEpic$,
  getFolderUsersPermissionsEpic$,
  mappingPermissionEpic$,
  addPermissionEpic$,
  delPermissionEpic$,
  getPermissionUserFolderEpic$,
  getRolesEpic$,
  getRolePermissionFolderEpic$,
  roleMappingLabelEpic$,
  revokeOwnershipEpic$,
  getFileUserPermissionsEpic$,
];
